import { DEFAULT_FILTERS } from "./filters";
import type {
  ComparisonFilters,
  HistoryWindow,
  PriceFilterMode,
} from "./types";

const NUMBER_PARAMS = {
  minBuyPrice: "buy_min",
  maxBuyPrice: "buy_max",
  minBuyOrders: "buy_orders_min",
  maxBuyOrders: "buy_orders_max",
  minSellPrice: "sell_min",
  maxSellPrice: "sell_max",
  minSellOrders: "sell_orders_min",
  maxSellOrders: "sell_orders_max",
  minProfitPct: "roi_min",
  maxProfitPct: "roi_max",
  minDailySales: "daily_sales_min",
  minMedianDailySales: "sales_day_min",
  minActiveDaysPct: "active_days_min",
  maxExpectedSaleDays: "exit_days_max",
  minSafeVolumeProfit: "volume_profit_min",
  minMedianPrice: "median_min",
  maxMedianPrice: "median_max",
  minAveragePrice: "average_min",
  maxAveragePrice: "average_max",
} as const;

type NumberFilterKey = keyof typeof NUMBER_PARAMS;

export interface ComparisonUrlState {
  filters: ComparisonFilters;
  searchQuery: string;
}

function readNumber(params: URLSearchParams, key: string): number | null {
  const value = params.get(key);
  if (value === null || value.trim() === "") {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function readMarkets(
  params: URLSearchParams,
  key: string,
  fallback: string[],
): string[] {
  const value = params.get(key);
  if (!value) {
    return [...fallback];
  }

  const markets = value
    .split(",")
    .map((market) => market.trim())
    .filter(Boolean);
  return markets.length > 0 ? [...new Set(markets)] : [...fallback];
}

function readWindow(value: string | null): HistoryWindow {
  if (value === "14") {
    return 14;
  }
  if (value === "60") {
    return 60;
  }
  return 30;
}

function readPriceMode(value: string | null): PriceFilterMode {
  return value === "median" || value === "average" ? value : "current";
}

function sameMarkets(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((market, index) => market === right[index]);
}

export function getComparisonUrlState(): ComparisonUrlState {
  if (typeof window === "undefined") {
    return { filters: { ...DEFAULT_FILTERS }, searchQuery: "" };
  }

  const params = new URLSearchParams(window.location.search);
  const priceFilterMode = readPriceMode(params.get("price_mode"));
  const filters: ComparisonFilters = {
    ...DEFAULT_FILTERS,
    buyMarkets: readMarkets(params, "buy", DEFAULT_FILTERS.buyMarkets),
    sellMarkets: readMarkets(params, "sell", DEFAULT_FILTERS.sellMarkets),
    priceFilterMode,
    medianWindowDays: readWindow(params.get("history")),
    medianWeighted: params.get("weighted") !== "0",
    profitableSpreadPct: readNumber(params, "best_roi") ?? DEFAULT_FILTERS.profitableSpreadPct,
    profitSort: params.get("sort") === "asc" ? "asc" : "desc",
    hideStickers: params.get("hide_stickers") === "1",
    hideNegativeDeals: params.get("hide_negative") === "1",
  };

  for (const [key, param] of Object.entries(NUMBER_PARAMS) as [
    NumberFilterKey,
    string,
  ][]) {
    filters[key] = readNumber(params, param);
  }

  return {
    filters,
    searchQuery: params.get("q")?.trim() ?? "",
  };
}

export function comparisonUrlSearch(
  filters: ComparisonFilters,
  searchQuery: string,
): string {
  const params = new URLSearchParams();

  if (searchQuery.trim()) {
    params.set("q", searchQuery.trim());
  }
  if (!sameMarkets(filters.buyMarkets, DEFAULT_FILTERS.buyMarkets)) {
    params.set("buy", filters.buyMarkets.join(","));
  }
  if (!sameMarkets(filters.sellMarkets, DEFAULT_FILTERS.sellMarkets)) {
    params.set("sell", filters.sellMarkets.join(","));
  }
  if (filters.priceFilterMode !== DEFAULT_FILTERS.priceFilterMode) {
    params.set("price_mode", filters.priceFilterMode);
  }
  if (filters.medianWindowDays !== DEFAULT_FILTERS.medianWindowDays) {
    params.set("history", String(filters.medianWindowDays));
  }
  if (filters.medianWeighted !== DEFAULT_FILTERS.medianWeighted) {
    params.set("weighted", filters.medianWeighted ? "1" : "0");
  }
  if (filters.profitableSpreadPct !== DEFAULT_FILTERS.profitableSpreadPct) {
    params.set("best_roi", String(filters.profitableSpreadPct));
  }
  if (filters.profitSort !== DEFAULT_FILTERS.profitSort) {
    params.set("sort", filters.profitSort);
  }
  if (filters.hideStickers) {
    params.set("hide_stickers", "1");
  }
  if (filters.hideNegativeDeals) {
    params.set("hide_negative", "1");
  }

  for (const [key, param] of Object.entries(NUMBER_PARAMS) as [
    NumberFilterKey,
    string,
  ][]) {
    const value = filters[key];
    if (value !== null) {
      params.set(param, String(value));
    }
  }

  const value = params.toString();
  return value ? `?${value}` : "";
}
